/**
 * Hardened entropy source for seed-phrase and key generation.
 *
 * Background: the Coldcard Mk3 advisory
 * (blog.coinkite.com/coldcard-mk3-seed-generation-warning/) describes a
 * device whose single hardware RNG silently produced far less than the
 * advertised 128 bits, and nothing in the seed-generation path noticed.
 * Users who had mixed in independent entropy (dice rolls) were fine;
 * everyone else generated weak seeds for nine firmware releases.
 *
 * Two lessons, both implemented here:
 *
 *   1. NEVER silently degrade. If the platform CSPRNG is missing or
 *      visibly broken we throw — we do not fall back to anything weaker.
 *      There is no `Math.random()` path in this module, by design.
 *
 *   2. NEVER trust a single source. Every generated secret is the output
 *      of HKDF over *several independent* CSPRNG draws plus an auxiliary
 *      pool (passive user input, timing jitter). Because a primary
 *      `getRandomValues` draw is always part of the HKDF input keying
 *      material, and HKDF-Extract is a PRF, the result is *at least* as
 *      unpredictable as the raw CSPRNG — mixing can never make it worse.
 *      But if the CSPRNG is the thing that fails, the aux pool still
 *      stands between an attacker and the seed.
 *
 * A note on honesty: the health checks below detect *catastrophic*
 * failure (stuck bits, constant output, a repeating short period). They
 * are not a statistical test suite and cannot detect a well-formed but
 * predictable generator. Nothing running inside a browser can. The
 * defence against that case is the mixing in `generateEntropyBytes`.
 */

import { hkdf } from '@noble/hashes/hkdf';
import { sha512 } from '@noble/hashes/sha512';
import { entropyToMnemonic, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';

/** Thrown when no usable CSPRNG exists, or the one we have looks broken. */
export class EntropyUnavailableError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'EntropyUnavailableError';
	}
}

const HKDF_INFO = new TextEncoder().encode('winbit32/seed-entropy/v1');

/** Valid BIP-39 entropy strengths, in bits. */
export type EntropyStrength = 128 | 160 | 192 | 224 | 256;

// ── 1. The platform CSPRNG, with no fallback ─────────────────────────

function getCryptoOrThrow(): Crypto {
	const c: Crypto | undefined =
		typeof globalThis !== 'undefined' ? (globalThis.crypto as Crypto | undefined) : undefined;
	if (!c || typeof c.getRandomValues !== 'function') {
		throw new EntropyUnavailableError(
			'No cryptographically secure random number generator is available in this ' +
			'environment. Key generation has been refused rather than fall back to a ' +
			'weak source. Use a current browser over HTTPS.',
		);
	}
	return c;
}

/**
 * Raw draw from the platform CSPRNG, with a cheap stuck-output guard.
 *
 * The all-zero / all-ones rejection costs one comparison and has a false
 * positive probability of 2^-(8n-1); for n >= 8 that is beyond
 * negligible. It exists because "returns a buffer of zeros" is the most
 * common way a broken or unimplemented RNG shim actually fails.
 */
export function secureRandomBytes(length: number): Uint8Array {
	if (!Number.isInteger(length) || length <= 0) {
		throw new TypeError(`secureRandomBytes: invalid length ${length}`);
	}
	const c = getCryptoOrThrow();
	const out = new Uint8Array(length);
	// getRandomValues is capped at 65536 bytes per call.
	for (let off = 0; off < length; off += 65536) {
		c.getRandomValues(out.subarray(off, Math.min(off + 65536, length)));
	}
	if (length >= 8 && (isConstant(out, 0x00) || isConstant(out, 0xff))) {
		throw new EntropyUnavailableError(
			'The system random number generator returned a constant value. It is ' +
			'broken or unavailable; key generation refused.',
		);
	}
	return out;
}

function isConstant(bytes: Uint8Array, value: number): boolean {
	for (let i = 0; i < bytes.length; i++) if (bytes[i] !== value) return false;
	return true;
}

// ── 2. RNG health self-test ──────────────────────────────────────────

export interface RngHealthReport {
	ok: boolean;
	/** Distinct byte values seen in a 1024-byte draw (uniform ≈ 251/256). */
	distinctByteValues: number;
	/** Whether two independent draws differed. */
	drawsDiffer: boolean;
	/** Populated when `ok` is false. */
	failure?: string;
}

let cachedHealth: RngHealthReport | null = null;

/**
 * Probe the CSPRNG for catastrophic failure.
 *
 * Checks, in order of what actually goes wrong in the field:
 *   - two independent draws are identical (a stuck or replayed source);
 *   - a 1024-byte draw is constant;
 *   - a 1024-byte draw covers implausibly few distinct byte values.
 *
 * For a uniform source the expected number of distinct values in 1024
 * bytes is 256 * (1 - (255/256)^1024) ≈ 251. We fail below 200, which a
 * healthy generator will not hit in the lifetime of the universe but
 * which a short-period or low-entropy generator hits immediately.
 *
 * Cached after the first successful run — this is a boot-time smoke
 * test, not a per-call gate (per-call guards live in `secureRandomBytes`).
 */
export function checkRngHealth(force = false): RngHealthReport {
	if (cachedHealth && !force) return cachedHealth;

	const fail = (failure: string, partial: Partial<RngHealthReport> = {}): RngHealthReport => ({
		ok: false,
		distinctByteValues: 0,
		drawsDiffer: false,
		...partial,
		failure,
	});

	let a: Uint8Array;
	let b: Uint8Array;
	try {
		a = secureRandomBytes(1024);
		b = secureRandomBytes(1024);
	} catch (err) {
		cachedHealth = fail(err instanceof Error ? err.message : String(err));
		return cachedHealth;
	}

	const drawsDiffer = !equalBytes(a, b);
	if (!drawsDiffer) {
		cachedHealth = fail(
			'Two independent random draws were identical — the generator is stuck.',
			{ drawsDiffer: false },
		);
		return cachedHealth;
	}

	const seen = new Set<number>();
	for (let i = 0; i < a.length; i++) seen.add(a[i]);
	const distinctByteValues = seen.size;
	if (distinctByteValues < 200) {
		cachedHealth = fail(
			`Random output covered only ${distinctByteValues} of 256 possible byte ` +
			'values — far below the ~251 expected. The generator is not producing ' +
			'full-entropy output.',
			{ drawsDiffer, distinctByteValues },
		);
		return cachedHealth;
	}

	cachedHealth = { ok: true, distinctByteValues, drawsDiffer };
	return cachedHealth;
}

function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
	return true;
}

function assertRngHealthy(): void {
	const health = checkRngHealth();
	if (!health.ok) {
		throw new EntropyUnavailableError(
			`Random number generator self-test failed: ${health.failure} ` +
			'Key generation refused — do not use any key material produced by this device.',
		);
	}
}

// ── 3. Auxiliary entropy pool ────────────────────────────────────────

/**
 * A 64-byte pool stirred by SHA-512. Contributions are *additional*
 * hardening, never a substitute for the CSPRNG: an empty pool costs
 * nothing, and a poisoned pool cannot weaken the output because the
 * CSPRNG draw is always mixed in alongside it.
 */
let pool: Uint8Array = new Uint8Array(64);
let poolContributions = 0;

/**
 * Stir arbitrary data into the auxiliary pool.
 *
 * Callers may pass anything with some unpredictability: pointer
 * coordinates, keystroke timings, dice rolls, device motion. No claim of
 * a specific entropy count is made or needed.
 */
export function addEntropy(data: string | number | Uint8Array | ArrayBuffer): void {
	let bytes: Uint8Array;
	if (typeof data === 'string') {
		bytes = new TextEncoder().encode(data);
	} else if (typeof data === 'number') {
		const buf = new ArrayBuffer(8);
		new DataView(buf).setFloat64(0, data);
		bytes = new Uint8Array(buf);
	} else if (data instanceof Uint8Array) {
		bytes = data;
	} else {
		bytes = new Uint8Array(data);
	}

	const jitter = timingJitter();
	const next = new Uint8Array(pool.length + bytes.length + jitter.length);
	next.set(pool, 0);
	next.set(bytes, pool.length);
	next.set(jitter, pool.length + bytes.length);
	pool = sha512(next);
	poolContributions++;
}

/** High-resolution timing snapshot — a weak but genuinely independent source. */
function timingJitter(): Uint8Array {
	const buf = new ArrayBuffer(16);
	const view = new DataView(buf);
	const now = typeof performance !== 'undefined' && typeof performance.now === 'function'
		? performance.now()
		: 0;
	view.setFloat64(0, now);
	view.setFloat64(8, Date.now());
	return new Uint8Array(buf);
}

/** How many times the auxiliary pool has been stirred this session. */
export function entropyPoolContributions(): number {
	return poolContributions;
}

// ── 4. Passive collection from user input ────────────────────────────

let collecting = false;
let lastCollect = 0;

/**
 * Begin passively stirring pointer/keyboard/motion events into the pool.
 *
 * This is the no-UI equivalent of Coldcard's dice rolls: by the time a
 * user has navigated to "create wallet" they have generated hundreds of
 * input events whose exact sub-millisecond timings are not available to
 * a remote attacker. Throttled to ~20/s so it costs nothing.
 *
 * Safe to call more than once; a no-op outside the browser.
 */
export function startEntropyCollection(): () => void {
	if (collecting || typeof window === 'undefined' || typeof document === 'undefined') {
		return () => {};
	}
	collecting = true;

	const onEvent = (ev: Event) => {
		const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
		if (now - lastCollect < 50) return; // ~20 samples/second, max
		lastCollect = now;

		const parts: number[] = [now];
		const pe = ev as PointerEvent & TouchEvent & KeyboardEvent & DeviceMotionEvent;
		if (typeof pe.clientX === 'number') parts.push(pe.clientX, pe.clientY);
		if (typeof pe.pressure === 'number') parts.push(pe.pressure);
		if (pe.touches && pe.touches[0]) parts.push(pe.touches[0].clientX, pe.touches[0].clientY);
		if (typeof pe.keyCode === 'number') parts.push(pe.keyCode);
		const acc = pe.accelerationIncludingGravity;
		if (acc) parts.push(acc.x || 0, acc.y || 0, acc.z || 0);

		const buf = new ArrayBuffer(parts.length * 8);
		const view = new DataView(buf);
		parts.forEach((p, i) => view.setFloat64(i * 8, p));
		addEntropy(new Uint8Array(buf));
	};

	const opts: AddEventListenerOptions = { passive: true, capture: true };
	const events = ['pointermove', 'pointerdown', 'keydown', 'touchmove', 'wheel'];
	events.forEach(name => window.addEventListener(name, onEvent, opts));
	if (typeof window.DeviceMotionEvent !== 'undefined') {
		window.addEventListener('devicemotion', onEvent, opts);
	}

	return () => {
		events.forEach(name => window.removeEventListener(name, onEvent, opts));
		window.removeEventListener('devicemotion', onEvent, opts);
		collecting = false;
	};
}

// ── 5. The generator ─────────────────────────────────────────────────

/**
 * Produce `byteLength` bytes suitable for use as key material.
 *
 * Construction:
 *
 *   IKM  = csprng(64) ‖ pool ‖ csprng(64) ‖ jitter
 *   salt = csprng(32)
 *   out  = HKDF-SHA512(IKM, salt, "winbit32/seed-entropy/v1", byteLength)
 *
 * Two separate CSPRNG draws bracket the pool so that a pool contributed
 * by hostile code still cannot choose the surrounding material, and the
 * output stays full-entropy if *either* the CSPRNG or the pool is sound.
 *
 * @param extra Optional caller-supplied entropy (e.g. dice rolls) mixed
 *              in for this call only, on top of the standing pool.
 */
export function generateEntropyBytes(byteLength: number, extra?: string | Uint8Array): Uint8Array {
	assertRngHealthy();
	if (extra !== undefined) addEntropy(extra);

	const primary = secureRandomBytes(64);
	const secondary = secureRandomBytes(64);

	// Liveness check, here rather than only at boot: `checkRngHealth` is
	// cached, so a generator that passed at startup and became stuck
	// later would otherwise slip through. We already need two independent
	// draws for the mixing, so comparing them is free — and for a working
	// generator the false-positive probability is 2^-512.
	if (equalBytes(primary, secondary)) {
		throw new EntropyUnavailableError(
			'Two independent random draws taken moments apart were identical — the ' +
			'random number generator is stuck. Key generation refused.',
		);
	}

	const salt = secureRandomBytes(32);
	const jitter = timingJitter();

	const ikm = new Uint8Array(primary.length + pool.length + secondary.length + jitter.length);
	let off = 0;
	ikm.set(primary, off); off += primary.length;
	ikm.set(pool, off); off += pool.length;
	ikm.set(secondary, off); off += secondary.length;
	ikm.set(jitter, off);

	const out = hkdf(sha512, ikm, salt, HKDF_INFO, byteLength);

	// Stir the used material forward so two calls in the same tick can
	// never share pool state.
	addEntropy(secondary);
	ikm.fill(0);
	primary.fill(0);
	secondary.fill(0);

	return out;
}

/**
 * Generate a BIP-39 mnemonic from the hardened entropy path.
 *
 * Drop-in replacement for `generateMnemonic(wordlist, strength)`, with
 * the self-test, multi-source mixing, and a round-trip validation of the
 * result before it is handed to the caller.
 */
export function generateSecureMnemonic(
	strength: EntropyStrength = 128,
	extra?: string | Uint8Array,
): string {
	if (strength % 32 !== 0 || strength < 128 || strength > 256) {
		throw new TypeError(`Invalid entropy strength: ${strength}`);
	}
	const entropy = generateEntropyBytes(strength / 8, extra);
	const mnemonic = entropyToMnemonic(entropy, wordlist);
	entropy.fill(0);

	// Cheap insurance against a broken wordlist or encoder shipping a
	// phrase that will not restore. Costs one PBKDF2-free checksum pass.
	if (!validateMnemonic(mnemonic, wordlist)) {
		throw new Error('Generated mnemonic failed its own checksum validation — refusing to return it.');
	}
	return mnemonic;
}

/** Diagnostics for a settings/about panel. */
export function describeEntropySources(): {
	csprng: string;
	health: RngHealthReport;
	poolContributions: number;
	passiveCollection: boolean;
} {
	let csprng = 'unavailable';
	try {
		getCryptoOrThrow();
		csprng = 'crypto.getRandomValues (platform CSPRNG)';
	} catch { /* leave as unavailable */ }
	return {
		csprng,
		health: checkRngHealth(),
		poolContributions,
		passiveCollection: collecting,
	};
}
